import { create } from 'zustand';
import { checkServerHealth } from '@/services/healthService';
import { MAINTENANCE_CONFIG } from '@/config/maintenance';

interface MaintenanceState {
  isMaintenance: boolean;
  isChecking: boolean;
  lastCheckedAt: number | null;
  setMaintenance: (value: boolean) => void;
  triggerHealthCheck: (sourceEndpoint?: string) => Promise<boolean>;
  retryService: () => Promise<boolean>;
}

const isDev = import.meta.env.VITE_APP_ENV !== 'production';
const hasTestQuery =
  isDev && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('testMaintenance') === 'true';

export const useMaintenanceStore = create<MaintenanceState>((set, get) => ({
  isMaintenance: hasTestQuery,
  isChecking: false,
  lastCheckedAt: null,

  setMaintenance: (value: boolean) => set({ isMaintenance: value }),

  /**
   * 핵심 API 장애 발생 시 서버 헬스체크를 수행하여 점검 화면 노출 여부 결정
   * @returns true: 점검 상태(장애 발생), false: 서버 정상(국소적 에러)
   */
  triggerHealthCheck: async () => {
    // 이미 점검 중 상태인 경우 추가 확인 없이 점검 상태 유지
    if (get().isMaintenance) {
      return true;
    }

    const now = Date.now();
    const lastChecked = get().lastCheckedAt;

    // 단시간 내 중복 검사 방지 (쿨타임)
    if (lastChecked && now - lastChecked < MAINTENANCE_CONFIG.RETRY_COOLDOWN_MS) {
      return get().isMaintenance;
    }

    set({ isChecking: true, lastCheckedAt: now });

    const result = await checkServerHealth();
    set({ isChecking: false });

    if (!result.isHealthy) {
      // 헬스체크 비정상(200 이외 또는 timeout) ➡️ 전체 점검 화면 노출
      set({ isMaintenance: true });
      return true;
    }

    // 헬스체크 정상 ➡️ 전체 점검 화면 미노출 (해당 개별 요청만 기존 오류 처리)
    return false;
  },

  /**
   * 점검 화면에서 사용자가 [다시 시도] 버튼을 눌렀을 때 복구 여부 확인
   * @returns true: 복구 성공 (서버 200 OK), false: 여전히 점검 중
   */
  retryService: async () => {
    const now = Date.now();
    const lastChecked = get().lastCheckedAt;

    // 연타 방지: 쿨타임 이내 재클릭 시 기존 상태 유지
    if (lastChecked && now - lastChecked < MAINTENANCE_CONFIG.RETRY_COOLDOWN_MS) {
      return !get().isMaintenance;
    }

    set({ isChecking: true, lastCheckedAt: now });

    const result = await checkServerHealth();
    set({ isChecking: false });

    if (result.isHealthy) {
      // 서버 복구 성공 ➡️ 점검 화면 닫기
      set({ isMaintenance: false });
      return true;
    }

    // 아직 점검 중
    set({ isMaintenance: true });
    return false;
  },
}));
